import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// ----- Imports ----- //
import { css, ThemeProvider } from '@emotion/react';
import { brandAlt, from, headlineBold34, headlineBold42, palette, space, textEgyptian17, } from '@guardian/source/foundations';
import { buttonThemeBrand, LinkButton, SvgArrowDownStraight, } from '@guardian/source/react-components';
import CentredContainer from 'components/containers/centredContainer';
import GridImage from 'components/gridImage/gridImage';
import Hero from 'components/page/hero';
import OfferStrapline from 'components/page/offerStrapline';
import PageTitle from 'components/page/pageTitle';
import { getMaxSavingVsRetail } from 'helpers/productPrice/paperSavingsVsRetail';
import { promotionHTML } from 'helpers/productPrice/promotions';
import { sendTrackingEventsOnClick } from 'helpers/productPrice/subscriptions';
import { offerStraplineBlue } from 'stylesheets/emotion/colours';
import { getDiscountCopy } from './discountCopy';
const heroCopy = css `
	padding: 0 ${space[3]}px ${space[3]}px;
	${from.tablet} {
		padding-bottom: ${space[9]}px;
	}
	${from.desktop} {
		padding-bottom: ${space[24]}px;
	}
`;
const heroTitle = css `
	${headlineBold34};
	margin-bottom: ${space[3]}px;

	${from.tablet} {
		${headlineBold42};
	}
`;
const heroTitleHighlight = css `
	color: ${brandAlt[400]};
`;
const heroParagraph = css `
	${textEgyptian17};
	line-height: 1.4;
	margin-bottom: ${space[6]}px;

	/* apply the same margin to paragraphs parsed from markdown from promo codes */
	& p:not(:last-of-type) {
		margin-bottom: ${space[9]}px;
	}

	${from.desktop} {
		max-width: 75%;
		margin-bottom: ${space[9]}px;
	}
`;
const mobileLineBreak = css `
	${from.tablet} {
		display: none;
	}
`;
const tabletLineBreak = css `
	${from.desktop} {
		display: none;
	}
`;
const defaultTitle = (_jsxs(_Fragment, { children: ["Guardian ", _jsx("br", { css: mobileLineBreak }), "and\u00A0Observer ", _jsx("br", { css: mobileLineBreak }), "newspaper subscriptions ", _jsx("br", { css: tabletLineBreak }), _jsx("span", { css: heroTitleHighlight, children: "to suit every reader" })] }));
const defaultCopy = (_jsx(_Fragment, { children: "We offer a range of packages from every day to weekend, and different subscription types depending on whether you want to collect your newspaper in a shop or get it delivered." }));
export function PaperHero({ productPrices, promotionCopy, }) {
    const maxSavingVsRetail = getMaxSavingVsRetail(productPrices) ?? 0;
    const { roundel } = getDiscountCopy(maxSavingVsRetail);
    const defaultRoundelText = roundel.length ? roundel.join(' ') : undefined;
    const title = promotionCopy.title ?? defaultTitle;
    const copy = promotionHTML(promotionCopy.description, {
        tag: 'p',
    }) ?? defaultCopy;
    const roundelText = promotionCopy.roundel ?? defaultRoundelText;
    return (_jsx(PageTitle, { title: "Newspaper subscription", theme: "paper", children: _jsxs(CentredContainer, { children: [_jsx(OfferStrapline, { fgCol: palette.neutral[7], bgCol: offerStraplineBlue, copy: roundelText }), _jsx(Hero, { image: _jsx(GridImage, { gridId: "printCampaignHeroHD", srcSizes: [1000, 500, 140], sizes: "(max-width: 740px) 100%,\n            (max-width: 1067px) 150%,\n            500px", imgType: "png", altText: "Newspapers" }), hideRoundelBelow: "mobileMedium", roundelElement: undefined, children: _jsxs("section", { css: heroCopy, children: [_jsx("h2", { css: heroTitle, children: title }), _jsx("p", { css: heroParagraph, children: copy }), _jsx(ThemeProvider, { theme: buttonThemeBrand, children: _jsx(LinkButton, { onClick: sendTrackingEventsOnClick({
                                        id: 'options_cta_click',
                                        product: 'Paper',
                                        componentType: 'ACQUISITIONS_BUTTON',
                                    }), priority: "tertiary", iconSide: "right", icon: _jsx(SvgArrowDownStraight, {}), href: "#HomeDelivery", children: "See pricing options" }) })] }) })] }) }));
}
export function PriceCardsPaperHero({ productPrices, promotionCopy, }) {
    const maxSavingVsRetail = getMaxSavingVsRetail(productPrices) ?? 0;
    const { roundel } = getDiscountCopy(maxSavingVsRetail);
    const defaultRoundelText = roundel.length ? roundel.join(' ') : undefined;
    const roundelText = promotionCopy.roundel ?? defaultRoundelText;
    return (_jsx(PageTitle, { title: "Newspaper subscription", theme: "paper", children: _jsx(CentredContainer, { children: _jsx(OfferStrapline, { fgCol: palette.neutral[7], bgCol: offerStraplineBlue, copy: roundelText }) }) }));
}
